import React from "react";

export type NavScreen =
  | "home"
  | "myMatches"
  | "newMatch"
  | "newTeam"
  | "newLeague"
  | "leagues"
  | "more"
  | "support"
  | "players"
  | "teams"
  | "teamsIndoor"
  | "teamsT20"
  | "teamsODI";

interface BottomNavProps {
  currentScreen: NavScreen;
  onNavigate: (screen: NavScreen) => void;
  inMatch?: boolean;
}

export default function BottomNav({ currentScreen, onNavigate, inMatch }: BottomNavProps) {
  if (inMatch) return null;

  const teamActive =
    currentScreen === "teams" ||
    currentScreen === "teamsIndoor" ||
    currentScreen === "teamsT20" ||
    currentScreen === "teamsODI" ||
    currentScreen === "newTeam";
  const leagueActive = currentScreen === "leagues" || currentScreen === "newLeague";
  const moreActive = currentScreen === "more" || currentScreen === "support";

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50"
      style={{
        background: "linear-gradient(to top, rgba(2,18,12,0.97) 0%, rgba(3,27,19,0.95) 100%)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderTop: "1px solid rgba(255,255,255,0.07)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="max-w-md mx-auto grid grid-cols-5 px-1 pt-1 pb-1">
        <NavButton
          icon={<IconHome />}
          label="Home"
          active={currentScreen === "home" || currentScreen === "myMatches"}
          onClick={() => onNavigate("home")}
        />
        <NavButton
          icon={<IconPlus />}
          label="Team"
          active={teamActive}
          onClick={() => onNavigate("newTeam")}
        />

        {/* Central scoring button — realistic cricket ball, no label */}
        <div className="flex items-center justify-center py-1">
          <button
            onClick={() => onNavigate("newMatch")}
            className="relative group transition-transform duration-200 active:scale-90 motion-reduce:transition-none motion-reduce:active:scale-100"
            aria-label="Create or schedule match"
            style={{ touchTargetSize: "auto" } as React.CSSProperties}
          >
            {/* Soft drop shadow beneath the ball */}
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-full blur-md"
              style={{
                bottom: "-6px",
                width: "44px",
                height: "10px",
                background: "rgba(0,0,0,0.45)",
                opacity: 0.6,
              }}
              aria-hidden="true"
            />
            {/* Realistic cricket ball image */}
            <img
              src="/cricket-ball-nav.webp"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="relative w-[64px] h-[64px] -mt-6 select-none pointer-events-none"
              style={{
                filter: "drop-shadow(0 6px 10px rgba(120,10,10,0.55))",
              }}
            />
          </button>
        </div>

        <NavButton
          icon={<IconPlus />}
          label="League"
          active={leagueActive}
          onClick={() => onNavigate("newLeague")}
        />
        <NavButton
          icon={<IconMore />}
          label="More"
          active={moreActive}
          onClick={() => onNavigate("more")}
        />
      </div>
    </nav>
  );
}

function NavButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-colors duration-200 relative"
      aria-current={active ? "page" : undefined}
    >
      {active && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-[3px] rounded-full"
          style={{ background: "#00C980" }}
        />
      )}
      <div className={`mb-0.5 transition-colors duration-200 ${active ? "text-[#00C980]" : "text-[#B8CFC4]"}`}>
        {icon}
      </div>
      <span className={`text-[10px] font-semibold transition-colors duration-200 ${active ? "text-[#00C980]" : "text-[#B8CFC4]"}`}>
        {label}
      </span>
    </button>
  );
}

function IconHome() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}
